/**
 * The wire protocol — build spec §6. One definition, imported by both sides, so
 * a type error catches protocol drift.
 *
 * Design rule that outranks everything else here (§5): a `RoomView` is built
 * PER RECIPIENT on the server. There is no redaction flag and no client-side
 * filtering. If a field is not in your view, the bytes never reached your
 * socket.
 */

import type { Card, DeckId } from './content.js';

export type Phase = 'lobby' | 'clue' | 'guessing' | 'reveal' | 'final';

export interface PublicPlayer {
  id: string;
  name: string;
  score: number;
  online: boolean;
  isHost: boolean;
}

export interface GuessResult {
  playerId: string;
  value: number;
  points: number;
}

/** What a given socket is allowed to see. Built per recipient; never broadcast. */
export interface RoomView {
  code: string;
  phase: Phase;
  players: PublicPlayer[];
  /** 0-based. */
  round: number;
  totalRounds: number;
  giverId: string | null;
  /** Poles are public. */
  card: Card | null;
  clue: string | null;
  /** Redacted per §5: the giver during `clue`, everyone during `reveal`, nobody else. */
  target: number | null;
  /** Values only at `reveal`. During `guessing` you get `lockedIn` instead. */
  guesses: GuessResult[] | null;
  /** Player ids who have locked in a guess. Who, never what. */
  lockedIn: string[];
  /** Populated at reveal: the exact average of the guessers' points. */
  giverPoints: number | null;
  you: {
    id: string;
    isGiver: boolean;
    hasGuessed: boolean;
    isHost: boolean;
    /**
     * Whether this socket may send `next` / `endGame` right now. Normally the
     * host alone (§4.3 — one advancer, or six clients race). Opened to everyone
     * after the host sits on a reveal for HOST_IDLE_PROMOTE_MS; the server
     * dedupes by round index either way, so the race stays closed.
     */
    mayAdvance: boolean;
  };
  /**
   * Host-only escape hatches (§8), sent only to the host and only once the
   * 45-second timer has elapsed. Absent means "not offered".
   */
  hostAction: { kind: 'skipRound' | 'revealWithout'; names: string[] } | null;
  /** Set when the host seat moved on its own, so the UI can announce it. */
  hostChangedTo: string | null;
  /** Which spectrum deck this room is playing with. */
  deck: DeckId;
  /** Rounds discarded because the giver dropped — shown so the count makes sense. */
  skippedRounds: number;
  /**
   * Rounds that actually scored. The two-player co-op ceiling is derived from
   * this, not from `totalRounds`: a skipped round never offered its 8 points,
   * and a ceiling nobody could have reached is the wrong number to chase.
   */
  scoredRounds: number;
  /**
   * The previous round, resolved. Sent during `clue` only, and shown on the
   * waiting screen: five people otherwise spend the slowest creative minute in
   * the game staring at a name. This is the argument window, and it runs
   * concurrently with clue-writing instead of competing with it.
   */
  lastRound: {
    clue: string;
    target: number;
    card: Card;
    giverName: string;
    guesses: GuessResult[];
  } | null;
}

/**
 * Phase-scoped messages all carry the round they were composed for. Without it,
 * a `guess` frame stuck in a stalled socket's buffer can be delivered after the
 * host has already revealed and advanced, and it scores in the wrong round —
 * the phase check alone doesn't catch it because the new round is in `guessing`
 * too. One integer makes every one of these idempotent.
 */
export type ClientMsg =
  | { t: 'create'; name: string }
  | { t: 'join'; code: string; name: string; token?: string }
  | { t: 'start'; passes: 1 | 2; deck?: DeckId }
  | { t: 'clue'; round: number; text: string }
  | { t: 'guess'; round: number; value: number }
  | { t: 'next'; round: number }
  | { t: 'endGame'; round: number }
  | { t: 'skipRound'; round: number }
  | { t: 'revealNow'; round: number }
  | { t: 'kick'; playerId: string }
  | { t: 'ping' };

/**
 * Application close codes. RFC 6455 §7.4.2 reserves 4000–4999 for exactly this.
 * The client's reconnect policy is keyed on them: 1012 means "back in seconds,
 * retry immediately"; 4001/4003/4004 are terminal and must not be retried.
 */
export const CLOSE = {
  /** IANA 1012 Service Restart — sent to every socket on SIGTERM. */
  SERVICE_RESTART: 1012,
  /** IANA 1013 Try Again Later — capacity. */
  TRY_LATER: 1013,
  /** Host kicked this player. Terminal. */
  KICKED: 4001,
  /** The same seat rejoined on a newer socket. This one is a corpse. Terminal. */
  SESSION_REPLACED: 4003,
  /** Socket never sent a valid create/join. Terminal. */
  NEVER_JOINED: 4004,
} as const;

export type ServerMsg =
  | { t: 'joined'; code: string; playerId: string; token: string; view: RoomView }
  | { t: 'view'; view: RoomView }
  | { t: 'error'; code: ErrorCode; message: string }
  | { t: 'pong' };

export type ErrorCode =
  | 'ROOM_NOT_FOUND'
  | 'ROOM_FULL'
  | 'GAME_IN_PROGRESS'
  | 'NAME_TAKEN'
  | 'NOT_HOST'
  | 'NOT_GIVER'
  | 'WRONG_PHASE'
  | 'ALREADY_GUESSED'
  | 'BAD_INPUT'
  | 'RATE_LIMITED'
  | 'SERVER_ERROR'
  /** Pushed to every socket from the SIGTERM handler before the process exits. */
  | 'SERVER_RESTARTING';

/** Human-facing text for each error: what happened, and what to do about it. */
export const ERROR_TEXT: Record<ErrorCode, string> = {
  ROOM_NOT_FOUND: "That code doesn't match a room. Check it and try again.",
  ROOM_FULL: 'That room already has six players.',
  GAME_IN_PROGRESS: 'That game has already started. Ask them to start a new one.',
  NAME_TAKEN: 'Someone in the room is already using that name. Pick another.',
  NOT_HOST: 'Only the host can do that.',
  NOT_GIVER: "You're not writing the clue this round.",
  WRONG_PHASE: 'The round moved on. Give it a second.',
  ALREADY_GUESSED: "You've already locked in this round.",
  BAD_INPUT: "That didn't look right. Try again.",
  RATE_LIMITED: 'Slow down a moment.',
  SERVER_ERROR: 'Something went wrong on our end. Try again.',
  SERVER_RESTARTING: 'The game server is restarting.',
};

/** How long the host may sit on the reveal before everyone else can advance (§4.3 + UX Gate A). */
export const HOST_IDLE_PROMOTE_MS = 60_000;
/** A joiner who hits GAME_IN_PROGRESS retries on this cadence rather than giving up. */
export const REJOIN_RETRY_MS = 10_000;

// ── Limits, shared so the client can pre-validate and the server can enforce ──

export const MAX_PLAYERS = 6;
export const MIN_PLAYERS_REMOTE = 2;
export const NAME_MAX = 16;
export const CLUE_MAX = 60;
export const CODE_LENGTH = 4;
/** No I, O, 0 or 1 — these get read aloud and typed by people who are mildly drunk. */
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
/** Bytes. Anything larger is rejected without parsing (§9). */
export const MAX_MESSAGE_BYTES = 2048;
/** 30 messages per 10 seconds per connection (§9). */
export const RATE_LIMIT_MESSAGES = 30;
export const RATE_LIMIT_WINDOW_MS = 10_000;
/**
 * Client heartbeat interval (§8). This is the one that proves the tab's JS is
 * running AND that its frames are reaching us.
 */
export const PING_INTERVAL_MS = 5_000;
/** Server protocol-level ping. RFC 6455 §5.5.3 makes the browser answer in the stack. */
export const SERVER_PING_MS = 10_000;
/**
 * No inbound frame at all — pong or data — for this long and the socket path is
 * dead, so terminate it. A phone that loses its radio sends no FIN, and Linux
 * would not surface the broken path for ~15 minutes.
 */
export const LIVENESS_TIMEOUT_MS = 25_000;
/**
 * No inbound APPLICATION message for this long and the player is offline, even
 * if the socket is still answering protocol pings.
 *
 * These are genuinely different signals, and the difference is not theoretical:
 * under Chromium's offline emulation the network stack keeps answering `ping`
 * frames after the page has lost connectivity entirely, so a pong-only liveness
 * check never fires and every §8 escape hatch stays unreachable. The same shape
 * of failure happens in the wild whenever the socket survives but the page
 * cannot use it.
 *
 * Four missed heartbeats. Generous on purpose: browsers throttle timers in
 * backgrounded tabs, so a shorter window would grey out a player who glanced at
 * a text message. A player who comes back flips online again on their next
 * message, without a reconnect.
 */
export const APP_LIVENESS_MS = 20_000;
/** How long a player must be offline before the host is offered an escape hatch (§8). */
export const HOST_ESCAPE_AFTER_MS = 45_000;
/**
 * Backstop: offer the same hatch this long into a stuck phase regardless of
 * anyone's online flag, so the feature degrades to "late" rather than "never".
 */
export const PHASE_STUCK_MS = 120_000;
/**
 * Offline this long and the host seat moves to the longest-connected online
 * player. Must exceed APP_LIVENESS_MS: `offlineSince` is backdated to last
 * contact, so anything at or below it would migrate in the same tick that
 * marks the host offline — and a host who glanced at another app would lose
 * the seat with no grace at all.
 */
export const HOST_MIGRATE_AFTER_MS = 45_000;
/** Idle rooms are wiped after this (§7). Heartbeats do not count as activity. */
export const ROOM_IDLE_MS = 30 * 60_000;
/** A room nobody ever joined is swept far sooner — it is an enumeration target. */
export const EMPTY_ROOM_IDLE_MS = 5 * 60_000;

/**
 * Strip control characters, zero-width characters, and bidi overrides, then
 * collapse whitespace. Applied server-side to every name and clue (§9); the
 * client uses it too so what you type is what you see.
 */
export function sanitizeText(input: string): string {
  return input
    // C0 and C1 control characters, including newlines and tabs.
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ')
    // Zero-width space / non-joiner / joiner, bidi marks, embeddings and
    // overrides (U+202A-U+202E and U+2066-U+2069 are the Trojan Source
    // characters, CVE-2021-42574 — in a score list they let one name render
    // as another), word joiner, invisible operators, BOM.
    .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The key a name collides on. Case-folded and NFKC-normalized, so "emory",
 * "EMORY" and a fullwidth "ｅｍｏｒｙ" are one name in a six-player score list.
 */
export function nameKey(name: string): string {
  return sanitizeText(name).normalize('NFKC').toLowerCase();
}

/** Normalize a typed or pasted share code: uppercase, strip anything not in the alphabet. */
export function normalizeCode(input: string): string {
  return input
    .toUpperCase()
    .split('')
    .filter((ch) => CODE_ALPHABET.includes(ch))
    .join('')
    .slice(0, 6);
}
